import React, { useState, useEffect } from 'react';
import api from '../config/api';
import ItemForm from './ItemForm';
import ImageImportModal from './ImageImportModal';
import InventoryImportModal from './InventoryImportModal';
import ItemTypeSelectionModal from './ItemTypeSelectionModal';

function InventoryDetail({ inventory, onBack, onUpdate }) {
  const [items, setItems] = useState([]);
  const [showItemForm, setShowItemForm] = useState(false);
  const [showItemTypeSelection, setShowItemTypeSelection] = useState(false);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [showImageImport, setShowImageImport] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState(inventory.name);
  const [newDescription, setNewDescription] = useState(inventory.description || '');
  const [fieldsCollapsed, setFieldsCollapsed] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [viewByItemType, setViewByItemType] = useState(false);

  useEffect(() => {
    fetchItems();
  }, [inventory.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchItems = async () => {
    try {
      const response = await api.get(`/inventories/${inventory.id}/items`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveItem = async (itemData) => {
    try {
      if (editingItem) {
        const response = await api.put(`/inventories/${inventory.id}/items/${editingItem.id}`, itemData);
        setItems(items.map(item => item.id === editingItem.id ? response.data : item));
      } else {
        const response = await api.post(`/inventories/${inventory.id}/items`, itemData);
        setItems([...items, response.data]);
      }
      setShowItemForm(false);
      setEditingItem(null);
      setSelectedItemType(null);
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  const handleAddItemClick = () => {
    // Check if inventory has multiple item types
    if (inventory.item_types && inventory.item_types.length > 1) {
      setShowItemTypeSelection(true);
    } else {
      // Single item type or default, proceed directly to item form
      const itemType = inventory.item_types && inventory.item_types.length > 0 
        ? inventory.item_types[0] 
        : inventory.default_item_type;
      setSelectedItemType(itemType);
      setShowItemForm(true);
    }
  };

  const handleItemTypeSelected = (itemType) => {
    setSelectedItemType(itemType);
    setShowItemTypeSelection(false);
    setShowItemForm(true);
  };

  const handleCloseItemForm = () => {
    setShowItemForm(false);
    setEditingItem(null);
    setSelectedItemType(null);
  };

  const handleCloseItemTypeSelection = () => {
    setShowItemTypeSelection(false);
    setSelectedItemType(null);
  };

  const deleteItem = async (itemId) => {
    try {
      await api.delete(`/inventories/${inventory.id}/items/${itemId}`);
      setItems(items.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const editItem = (item) => {
    setEditingItem(item);
    
    // Find the item type for this item
    if (item.item_type_id && inventory.item_types) {
      const itemType = inventory.item_types.find(type => type.id === item.item_type_id);
      setSelectedItemType(itemType || inventory.default_item_type);
    } else {
      setSelectedItemType(inventory.default_item_type);
    }
    
    setShowItemForm(true);
  };

  const saveInventoryDetails = async () => {
    try {
      const response = await api.put(`/inventories/${inventory.id}`, {
        name: newTitle.trim(),
        description: newDescription.trim(),
        inventory_type_id: inventory.inventory_type_id,
        location_type_id: inventory.location_type_id
      });
      
      // Update the parent component with the new inventory data
      if (onUpdate) {
        onUpdate(response.data);
      }
      
      setEditingTitle(false);
    } catch (error) {
      console.error('Error updating inventory:', error);
      alert('Error updating inventory: ' + (error.response?.data?.error || error.message));
    }
  };

  const cancelEditTitle = () => {
    setNewTitle(inventory.name);
    setNewDescription(inventory.description || '');
    setEditingTitle(false);
  };

  const handleBulkImport = async (extractedItems) => {
    try {
      const promises = extractedItems.map(item => 
        api.post(`/inventories/${inventory.id}/items`, item)
      );
      
      const responses = await Promise.all(promises);
      const newItems = responses.map(response => response.data);
      
      setItems([...items, ...newItems]);
      setShowImageImport(false);
      
      // Show success message
      alert(`Successfully imported ${newItems.length} items!`);
    } catch (error) {
      console.error('Error importing items:', error);
      alert('Error importing items: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleExportInventory = async () => {
    try {
      const response = await api.get(`/inventories/${inventory.id}/export`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `inventory_${inventory.name.replace(/[^a-zA-Z0-9_-]/g, '_')}_export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('Inventory exported successfully!');
    } catch (error) {
      console.error('Error exporting inventory:', error);
      alert('Error exporting inventory: ' + (error.response?.data?.error || error.message));
    }
  };

  const groupItemsByType = () => {
    if (!inventory.item_types || inventory.item_types.length <= 1) {
      return { 'All Items': items };
    }

    const grouped = {};
    
    // Initialize groups for each item type
    inventory.item_types.forEach(itemType => {
      grouped[itemType.name] = [];
    });
    
    // Add 'Unknown Type' group for items without type
    grouped['Unknown Type'] = [];
    
    // Group items
    items.forEach(item => {
      if (item.item_type_id) {
        const itemType = inventory.item_types.find(type => type.id === item.item_type_id);
        if (itemType) {
          grouped[itemType.name].push(item);
        } else {
          grouped['Unknown Type'].push(item);
        }
      } else {
        grouped['Unknown Type'].push(item);
      }
    });
    
    // Remove empty groups
    Object.keys(grouped).forEach(key => {
      if (grouped[key].length === 0) {
        delete grouped[key];
      }
    });
    
    return grouped;
  };

  const renderItemsTable = (itemsToRender, itemTypeName = null) => {
    if (itemsToRender.length === 0) {
      return <p>No items in this {itemTypeName ? `${itemTypeName.toLowerCase()} ` : ''}group.</p>;
    }

    // Determine which fields to show based on the item type
    let fieldsToShow = [];
    if (itemTypeName && itemTypeName !== 'Unknown Type' && itemTypeName !== 'All Items') {
      const itemType = inventory.item_types?.find(type => type.name === itemTypeName);
      fieldsToShow = itemType?.fields?.filter(field => field.in_view !== false) || [];
    } else {
      fieldsToShow = inventory.fields?.filter(field => field.in_view !== false) || [];
    }

    return (
      <table className="items-table">
        <thead>
          <tr>
            {fieldsToShow.map((field, index) => (
              <th key={index}>{field.name}</th>
            ))}
            {inventory.locations && inventory.locations.length > 0 && (
              <th>Location</th>
            )}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {itemsToRender.map(item => (
            <tr key={item.id}>
              {fieldsToShow.map((field, index) => (
                <td key={index}>
                  {(() => {
                    const value = item.field_values && item.field_values[field.name];
                    if (!value) return '-';
                    
                    // Handle checkbox fields (arrays)
                    if (Array.isArray(value)) {
                      return value.length > 0 ? value.join(', ') : '-';
                    }
                    
                    return value;
                  })()}
                </td>
              ))}
              {inventory.locations && inventory.locations.length > 0 && (
                <td>
                  {(item.field_values && item.field_values['Location']) || '-'}
                </td>
              )}
              <td>
                <button className="btn" onClick={() => editItem(item)} style={{ marginRight: '0.5rem' }}>
                  Edit
                </button>
                <button 
                  className="btn btn-danger" 
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this item?')) {
                      deleteItem(item.id);
                    }
                  }}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const handleImportInventory = async (fileContent) => {
    if (!window.confirm(
      '⚠️ WARNING: This will replace ALL current items in this inventory!\n\n' +
      'The import will also update the inventory type and location type if they are different.\n\n' +
      'This action cannot be undone. Are you sure you want to continue?'
    )) {
      return;
    }

    try {
      const response = await api.post(`/inventories/${inventory.id}/import`, {
        type: fileContent.type,
        data: fileContent
      });
      
      const results = response.data.results;
      let message = '✅ Import completed successfully!\n\n';
      
      message += `• Imported ${results.imported_items} items\n`;
      
      if (results.updated_inventory) {
        message += '• Updated inventory details\n';
      }
      
      if (results.created_inventory_type) {
        message += '• Created new inventory type\n';
      }
      
      if (results.updated_inventory_type) {
        message += '• Updated existing inventory type\n';
      }
      
      if (results.created_location_type) {
        message += '• Created new location type\n';
      }
      
      if (results.updated_location_type) {
        message += '• Updated existing location type\n';
      }
      
      if (results.errors && results.errors.length > 0) {
        message += `\n⚠️ Warnings:\n${results.errors.slice(0, 3).join('\n')}`;
        if (results.errors.length > 3) {
          message += `\n...and ${results.errors.length - 3} more warnings`;
        }
      }
      
      alert(message);
      
      // Refresh the page to show updated data
      window.location.reload();
    } catch (error) {
      console.error('Error importing inventory:', error);
      alert('Error importing inventory: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return <div>Loading items...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' }}>
        <div style={{ flex: 1 }}>
          <button className="btn" onClick={onBack} style={{ marginBottom: '1rem' }}>
            ← Back to Inventories
          </button>
          
          {editingTitle ? (
            <div style={{ marginBottom: '1rem' }}>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold', 
                  width: '100%', 
                  marginBottom: '0.5rem',
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
              <textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Description (optional)"
                style={{ 
                  width: '100%', 
                  minHeight: '60px',
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  resize: 'vertical'
                }}
              />
              <div style={{ marginTop: '0.5rem' }}>
                <button 
                  className="btn btn-secondary" 
                  onClick={saveInventoryDetails}
                  style={{ marginRight: '0.5rem' }}
                  disabled={!newTitle.trim()}
                >
                  Save
                </button>
                <button 
                  className="btn" 
                  onClick={cancelEditTitle}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <h2 style={{ margin: 0 }}>{inventory.name}</h2>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setEditingTitle(true)}
                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                  title="Edit inventory title and description"
                >
                  ✏️ Edit
                </button>
              </div>
              {inventory.description && (
                <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>{inventory.description}</p>
              )}
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn" onClick={handleAddItemClick}>
            Add Item
          </button>
          <button className="btn" onClick={() => setShowImageImport(true)} style={{ backgroundColor: '#27ae60' }}>
            📷 Import from Image
          </button>
          <button className="btn" onClick={handleExportInventory} style={{ backgroundColor: '#3498db' }}>
            📤 Export Inventory
          </button>
          <button className="btn" onClick={() => setShowImportModal(true)} style={{ backgroundColor: '#e67e22' }}>
            📥 Import Inventory
          </button>
        </div>
      </div>

      <div className="card">
        <div 
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            cursor: 'pointer',
            userSelect: 'none'
          }}
          onClick={() => setFieldsCollapsed(!fieldsCollapsed)}
        >
          <h3 style={{ margin: 0 }}>
            Field Definitions ({inventory.fields ? inventory.fields.length : 0})
          </h3>
          <span style={{ fontSize: '1.2rem' }}>
            {fieldsCollapsed ? '▼' : '▲'}
          </span>
        </div>
        
        {!fieldsCollapsed && (
          <div style={{ marginTop: '1rem' }}>
            {inventory.fields && inventory.fields.length > 0 ? (
              <div>
                {inventory.fields.map((field, index) => (
                  <div key={index} className="field-definition" style={{ 
                    padding: '0.75rem', 
                    backgroundColor: field.in_view !== false ? '#f8f9fa' : '#fff3cd', 
                    marginBottom: '0.5rem',
                    borderRadius: '4px',
                    border: field.in_view !== false ? '1px solid #e9ecef' : '1px solid #ffeaa7'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <strong>{field.name}</strong>
                      <span style={{ color: '#666', fontSize: '0.9rem' }}>({field.type})</span>
                      {field.required && <span style={{ color: '#e74c3c' }}>*</span>}
                      {field.in_view !== false ? 
                        <span style={{ color: '#27ae60', fontSize: '0.7rem', backgroundColor: '#d4edda', padding: '0.1rem 0.3rem', borderRadius: '2px' }}>VISIBLE</span> :
                        <span style={{ color: '#856404', fontSize: '0.7rem', backgroundColor: '#fff3cd', padding: '0.1rem 0.3rem', borderRadius: '2px' }}>DETAIL ONLY</span>
                      }
                    </div>
                    {field.description && (
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#666' }}>
                        {field.description}
                      </p>
                    )}
                    {field.options && field.options.length > 0 && (
                      <div style={{ marginTop: '0.25rem' }}>
                        <span style={{ fontSize: '0.8rem', color: '#666' }}>Options: </span>
                        <span style={{ fontSize: '0.8rem' }}>{field.options.join(', ')}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#666', fontStyle: 'italic' }}>No fields defined for this inventory.</p>
            )}
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3>Items ({items.length})</h3>
          {inventory.item_types && inventory.item_types.length > 1 && (
            <button 
              className="btn btn-secondary"
              onClick={() => setViewByItemType(!viewByItemType)}
              style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
            >
              {viewByItemType ? '📋 View All' : '🏷️ View by Type'}
            </button>
          )}
        </div>
        
        {items.length === 0 ? (
          <p>No items in this inventory yet.</p>
        ) : viewByItemType ? (
          // Grouped view by item type
          <div>
            {Object.entries(groupItemsByType()).map(([typeName, typeItems]) => (
              <div key={typeName} style={{ marginBottom: '2rem' }}>
                <h4 style={{ 
                  color: '#333', 
                  borderBottom: '2px solid #007bff', 
                  paddingBottom: '0.5rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>{typeName}</span>
                  <span style={{ 
                    fontSize: '0.8rem', 
                    backgroundColor: '#007bff', 
                    color: 'white', 
                    padding: '0.2rem 0.5rem', 
                    borderRadius: '12px',
                    fontWeight: 'normal'
                  }}>
                    {typeItems.length}
                  </span>
                </h4>
                {renderItemsTable(typeItems, typeName)}
              </div>
            ))}
          </div>
        ) : (
          // Standard unified view
          renderItemsTable(items)
        )}
      </div>

      {showItemForm && (
        <ItemForm
          inventory={inventory}
          item={editingItem}
          selectedItemType={selectedItemType}
          availableItemTypes={inventory.item_types}
          onSave={saveItem}
          onClose={handleCloseItemForm}
          onItemTypeChange={setSelectedItemType}
        />
      )}

      {showImageImport && (
        <ImageImportModal
          inventory={inventory}
          onImport={handleBulkImport}
          onClose={() => setShowImageImport(false)}
        />
      )}
      
      {showImportModal && (
        <InventoryImportModal
          onImport={handleImportInventory}
          onClose={() => setShowImportModal(false)}
        />
      )}

      {showItemTypeSelection && (
        <ItemTypeSelectionModal
          inventory={inventory}
          onSelectItemType={handleItemTypeSelected}
          onClose={handleCloseItemTypeSelection}
        />
      )}
    </div>
  );
}

export default InventoryDetail;